import React, { useContext, useEffect, useMemo, useState } from 'react'

import { selectLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import { useAppSelector } from 'hooks/reduxHooks'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import MasterModalContext from 'contexts/MasterModalContext'
import ModalContext from 'contexts/ModalContext'
import { matchPath } from 'react-router'
import { isPaidSessionSelector } from 'selectors/marketingSelectors'
import { BUSINESS_TRAVELLER_LOGIN_ROUTE } from 'components/App/Routes/routeConstants'

const ShowLoginExcludedRoutes = [
  '/:regionCode/pay/:paymentLinkId',
  '/:regionCode/offer',
  '/:regionCode/tour',
  '/:regionCode/cruises',
  '/:regionCode/experience',
  '/:regionCode/partner',
  '/:regionCode/metasearch/:propertyId',
  '/:regionCode/business-signup',
  '/:regionCode/business-signup-complete',
  '/:regionCode/business-traveller/business-signup',
  '/:regionCode/employee-setup',
  '/:regionCode/frequently-asked-questions',
  '/:regionCode/terms-and-conditions',
  '/:regionCode/privacy-policy',
  '/:regionCode/expression-of-interest',
  `/:regionCode${BUSINESS_TRAVELLER_LOGIN_ROUTE}`,
]

export const MODAL_OPENED_TIMEOUT_IN_SECONDS = 10

function useShowLogin() {
  const [hasShownThisAnonSession, setHasShownThisAnonSession] = useState<boolean>(false)
  const isLoggedIn = useAppSelector(isLoggedInSelector)
  const showModal = useContext(ModalContext)
  const modalState = useContext(MasterModalContext)
  const isPaidSession = useAppSelector(isPaidSessionSelector)

  const currentPath = useAppSelector(state => state.router.location.pathname)
  const isBlacklistedRoute = useMemo(() => {
    // If the current path is an excluded route or the current path is the homepage, do not show the login modal
    const isExcludedRoute = !!matchPath(currentPath, { path: ShowLoginExcludedRoutes, exact: false })
    const isHomePage = !!matchPath(currentPath, { path: '/:regionCode', exact: true })

    return isExcludedRoute || isHomePage
  }, [currentPath])

  // reset the session flag when the user logs in, otherwise the modal will not show again
  useEffect(() => {
    if (isLoggedIn && hasShownThisAnonSession) {
      setHasShownThisAnonSession(false)
    }
  }, [isLoggedIn, hasShownThisAnonSession])

  useEffect(() => {
    if (!isLoggedIn && !hasShownThisAnonSession && !modalState.open && !isPaidSession && !isBlacklistedRoute) {
      setHasShownThisAnonSession(true) // set the flag to true to prevent the modal from showing again
      showModal(<AccountAccessModal />)
    }
  }, [hasShownThisAnonSession, isLoggedIn, showModal, modalState, isPaidSession, isBlacklistedRoute])
}

export default useShowLogin
